import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import { useAuth, api } from '@halo/web-lib';
import type {
  DashboardPreferences,
  DashboardView,
  PinnedWidget,
  WidgetId,
  LayoutSection,
} from '../types/dashboard-preferences';
import { MAX_VIEWS } from '../types/dashboard-preferences';
import { DEFAULT_SECTIONS } from '../constants/platform-config';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface DashboardPreferencesStateValue {
  preferences: DashboardPreferences;
  activeView: DashboardView;
}

interface DashboardPreferencesActionsValue {
  // View CRUD
  switchView: (viewId: string) => void;
  createView: (name: string) => DashboardView | null;
  deleteView: (viewId: string) => void;
  renameView: (viewId: string, name: string) => void;

  // Pinned widgets
  pinWidget: (widget: PinnedWidget) => void;
  unpinWidget: (widgetId: WidgetId) => void;
  reorderPinnedWidgets: (orderedIds: WidgetId[]) => void;

  // Section management
  setSectionVisibility: (channelKey: string, visible: boolean) => void;
  setSectionCollapsed: (channelKey: string, collapsed: boolean) => void;
  reorderSections: (orderedChannelKeys: string[]) => void;

  // Widget filters & ordering
  setWidgetFilter: (channelKey: string, keys: string[] | null) => void;
  setWidgetOrder: (channelKey: string, orderedKeys: string[]) => void;
  setExcludeTikTokOrders: (value: boolean) => void;
}

const DashboardPreferencesStateContext = createContext<DashboardPreferencesStateValue | null>(null);
const DashboardPreferencesActionsContext = createContext<DashboardPreferencesActionsValue | null>(
  null,
);

// ─── Helpers ────────────────────────────────────────────────────────────────────

function storageKey(storeId: string): string {
  return `halo_dashboard_prefs_${storeId}`;
}

function createDefaultView(): DashboardView {
  return {
    id: crypto.randomUUID(),
    name: 'Default',
    layoutSections: DEFAULT_SECTIONS.map((s) => ({ ...s })),
    pinnedWidgets: [],
    widgetFilters: {},
    widgetOrder: {},
  };
}

function createDefaultPreferences(): DashboardPreferences {
  const view = createDefaultView();
  return { activeViewId: view.id, views: [view] };
}

function readFromStorage(storeId: string): DashboardPreferences | null {
  try {
    const raw = localStorage.getItem(storageKey(storeId));
    if (!raw) return null;
    return JSON.parse(raw) as DashboardPreferences;
  } catch {
    return null;
  }
}

function writeToStorage(storeId: string, prefs: DashboardPreferences): void {
  try {
    localStorage.setItem(storageKey(storeId), JSON.stringify(prefs));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

function getActiveView(prefs: DashboardPreferences): DashboardView {
  return prefs.views.find((v) => v.id === prefs.activeViewId) ?? prefs.views[0]!;
}

// ─── Provider ───────────────────────────────────────────────────────────────────

export function DashboardPreferencesProvider({ children }: { children: ReactNode }) {
  const { storeId } = useAuth();
  const [prefs, setPrefs] = useState<DashboardPreferences>(createDefaultPreferences);
  const apiSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialized = useRef(false);

  // Load preferences on mount (API first, then localStorage fallback)
  useEffect(() => {
    if (!storeId) return;

    let cancelled = false;

    async function load() {
      // Try API first
      try {
        const res = await api.get<DashboardPreferences>('/api/v1/dashboard/preferences');
        if (!cancelled && res.success && res.data && res.data.views.length > 0) {
          setPrefs(res.data);
          writeToStorage(storeId!, res.data);
          initialized.current = true;
          return;
        }
      } catch {
        // API not available — fall through to localStorage
      }

      // Fallback to localStorage
      if (!cancelled) {
        const stored = readFromStorage(storeId!);
        if (stored && stored.views.length > 0) {
          setPrefs(stored);
        } else {
          const defaults = createDefaultPreferences();
          setPrefs(defaults);
          writeToStorage(storeId!, defaults);
        }
        initialized.current = true;
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  // Persist helper: localStorage immediately, API debounced
  const persist = useCallback(
    (next: DashboardPreferences) => {
      if (!storeId) return;
      writeToStorage(storeId, next);

      if (apiSaveTimer.current) clearTimeout(apiSaveTimer.current);
      apiSaveTimer.current = setTimeout(() => {
        void api.put('/api/v1/dashboard/preferences', { preferences: next }).catch(() => {
          // API save failed — data is safe in localStorage
        });
      }, 500);
    },
    [storeId],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (apiSaveTimer.current) clearTimeout(apiSaveTimer.current);
    };
  }, []);

  // Immutable update helper
  const update = useCallback(
    (fn: (prev: DashboardPreferences) => DashboardPreferences) => {
      setPrefs((prev) => {
        const next = fn(prev);
        persist(next);
        return next;
      });
    },
    [persist],
  );

  // ─── View CRUD ──────────────────────────────────────────────

  const switchView = useCallback(
    (viewId: string) => {
      update((prev) => ({ ...prev, activeViewId: viewId }));
    },
    [update],
  );

  const createView = useCallback(
    (name: string): DashboardView | null => {
      let created: DashboardView | null = null;
      update((prev) => {
        if (prev.views.length >= MAX_VIEWS) return prev;
        const newView: DashboardView = {
          id: crypto.randomUUID(),
          name,
          layoutSections: DEFAULT_SECTIONS.map((s) => ({ ...s })),
          pinnedWidgets: [],
          widgetFilters: {},
          widgetOrder: {},
        };
        created = newView;
        return {
          activeViewId: newView.id,
          views: [...prev.views, newView],
        };
      });
      return created;
    },
    [update],
  );

  const deleteView = useCallback(
    (viewId: string) => {
      update((prev) => {
        const remaining = prev.views.filter((v) => v.id !== viewId);
        if (remaining.length === 0) return prev; // Don't delete the last view
        const activeViewId = prev.activeViewId === viewId ? remaining[0]!.id : prev.activeViewId;
        return { activeViewId, views: remaining };
      });
    },
    [update],
  );

  const renameView = useCallback(
    (viewId: string, name: string) => {
      update((prev) => ({
        ...prev,
        views: prev.views.map((v) => (v.id === viewId ? { ...v, name } : v)),
      }));
    },
    [update],
  );

  // ─── Pinned Widgets ─────────────────────────────────────────

  const pinWidget = useCallback(
    (widget: PinnedWidget) => {
      update((prev) => {
        const view = getActiveView(prev);
        if (view.pinnedWidgets.some((w) => w.id === widget.id)) return prev;
        const updatedView = { ...view, pinnedWidgets: [...view.pinnedWidgets, widget] };
        return {
          ...prev,
          views: prev.views.map((v) => (v.id === view.id ? updatedView : v)),
        };
      });
    },
    [update],
  );

  const unpinWidget = useCallback(
    (widgetId: WidgetId) => {
      update((prev) => {
        const view = getActiveView(prev);
        const updatedView = {
          ...view,
          pinnedWidgets: view.pinnedWidgets.filter((w) => w.id !== widgetId),
        };
        return {
          ...prev,
          views: prev.views.map((v) => (v.id === view.id ? updatedView : v)),
        };
      });
    },
    [update],
  );

  const reorderPinnedWidgets = useCallback(
    (orderedIds: WidgetId[]) => {
      update((prev) => {
        const view = getActiveView(prev);
        const pinMap = new Map(view.pinnedWidgets.map((w) => [w.id, w]));
        const reordered = orderedIds
          .map((id) => pinMap.get(id))
          .filter((w): w is PinnedWidget => w !== undefined);
        const updatedView = { ...view, pinnedWidgets: reordered };
        return {
          ...prev,
          views: prev.views.map((v) => (v.id === view.id ? updatedView : v)),
        };
      });
    },
    [update],
  );

  // ─── Section Management ─────────────────────────────────────

  const updateActiveViewSections = useCallback(
    (fn: (sections: LayoutSection[]) => LayoutSection[]) => {
      update((prev) => {
        const view = getActiveView(prev);
        const updatedView = { ...view, layoutSections: fn(view.layoutSections) };
        return {
          ...prev,
          views: prev.views.map((v) => (v.id === view.id ? updatedView : v)),
        };
      });
    },
    [update],
  );

  const setSectionVisibility = useCallback(
    (channelKey: string, visible: boolean) => {
      updateActiveViewSections((sections) =>
        sections.map((s) => (s.channelKey === channelKey ? { ...s, visible } : s)),
      );
    },
    [updateActiveViewSections],
  );

  const setSectionCollapsed = useCallback(
    (channelKey: string, collapsed: boolean) => {
      updateActiveViewSections((sections) =>
        sections.map((s) => (s.channelKey === channelKey ? { ...s, collapsed } : s)),
      );
    },
    [updateActiveViewSections],
  );

  const reorderSections = useCallback(
    (orderedChannelKeys: string[]) => {
      updateActiveViewSections((sections) => {
        const sectionMap = new Map(sections.map((s) => [s.channelKey, s]));
        return orderedChannelKeys
          .map((key) => sectionMap.get(key))
          .filter((s): s is LayoutSection => s !== undefined);
      });
    },
    [updateActiveViewSections],
  );

  // ─── Widget Filters & Ordering ──────────────────────────────

  const setWidgetFilter = useCallback(
    (channelKey: string, keys: string[] | null) => {
      update((prev) => {
        const view = getActiveView(prev);
        const updatedView = {
          ...view,
          widgetFilters: { ...view.widgetFilters, [channelKey]: keys },
        };
        return {
          ...prev,
          views: prev.views.map((v) => (v.id === view.id ? updatedView : v)),
        };
      });
    },
    [update],
  );

  const setWidgetOrder = useCallback(
    (channelKey: string, orderedKeys: string[]) => {
      update((prev) => {
        const view = getActiveView(prev);
        const updatedView = {
          ...view,
          widgetOrder: { ...view.widgetOrder, [channelKey]: orderedKeys },
        };
        return {
          ...prev,
          views: prev.views.map((v) => (v.id === view.id ? updatedView : v)),
        };
      });
    },
    [update],
  );

  const setExcludeTikTokOrders = useCallback(
    (value: boolean) => {
      update((prev) => {
        const view = getActiveView(prev);
        const updatedView = { ...view, excludeTikTokOrders: value };
        return {
          ...prev,
          views: prev.views.map((v) => (v.id === view.id ? updatedView : v)),
        };
      });
    },
    [update],
  );

  // ─── Context Value ──────────────────────────────────────────

  const activeView = getActiveView(prefs);

  const stateValue = useMemo(
    () => ({
      preferences: prefs,
      activeView,
    }),
    [prefs, activeView],
  );

  const actionsValue = useMemo(
    () => ({
      switchView,
      createView,
      deleteView,
      renameView,
      pinWidget,
      unpinWidget,
      reorderPinnedWidgets,
      setSectionVisibility,
      setSectionCollapsed,
      reorderSections,
      setWidgetFilter,
      setWidgetOrder,
      setExcludeTikTokOrders,
    }),
    [
      switchView,
      createView,
      deleteView,
      renameView,
      pinWidget,
      unpinWidget,
      reorderPinnedWidgets,
      setSectionVisibility,
      setSectionCollapsed,
      reorderSections,
      setWidgetFilter,
      setWidgetOrder,
      setExcludeTikTokOrders,
    ],
  );

  return (
    <DashboardPreferencesStateContext.Provider value={stateValue}>
      <DashboardPreferencesActionsContext.Provider value={actionsValue}>
        {children}
      </DashboardPreferencesActionsContext.Provider>
    </DashboardPreferencesStateContext.Provider>
  );
}

export function useDashboardPreferencesState(): DashboardPreferencesStateValue {
  const ctx = useContext(DashboardPreferencesStateContext);
  if (!ctx) {
    throw new Error(
      'useDashboardPreferencesState must be used within DashboardPreferencesProvider',
    );
  }
  return ctx;
}

export function useDashboardPreferencesActions(): DashboardPreferencesActionsValue {
  const ctx = useContext(DashboardPreferencesActionsContext);
  if (!ctx) {
    throw new Error(
      'useDashboardPreferencesActions must be used within DashboardPreferencesProvider',
    );
  }
  return ctx;
}
